import { useEffect, useRef, useState, FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d9jojcoh9dsfbuoi6500';

type FormStatus = 'idle' | 'sending' | 'success' | 'error';

export default function Contact() {
  const [visible, setVisible] = useState(false);
  const [formStatus, setFormStatus] = useState<FormStatus>('idle');
  const [formError, setFormError] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      formRef.current?.reset();
      return;
    }

    setFormStatus('sending');
    setFormError('');

    const data = new FormData(form);
    const params = new URLSearchParams();
    data.forEach((val, key) => {
      if (key !== 'website_alt') params.append(key, val.toString());
    });

    try {
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText;
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        formRef.current?.reset();
      } else {
        setFormStatus('error');
        setFormError(serverMsg || 'Error al enviar. Intenta de nuevo.');
      }
    } catch {
      setFormStatus('error');
      setFormError('Error de conexión. Por favor intenta de nuevo.');
    }
  };

  return (
    <section id="contacto" ref={ref} className="py-20 md:py-28 bg-background-950">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className={`text-center mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-accent-400 text-sm font-600 tracking-widest uppercase mb-3 block">
            Habla con Nosotros
          </span>
          <h2 className="font-heading font-700 text-4xl md:text-5xl text-white mb-4">
            <span className="gradient-text">Contáctanos</span>
          </h2>
          <div className="section-divider mx-auto mb-4"></div>
          <p className="text-foreground-400 text-base max-w-md mx-auto">
            Solicita información, cotizaciones o asesoría técnica sobre nuestros
            químicos industriales y fluidos de perforación.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Info */}
          <div className={`transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <h3 className="font-heading font-700 text-2xl text-white mb-6">
              Información de Contacto
            </h3>

            <div className="space-y-5 mb-8">
              {[
                {
                  icon: 'ri-map-pin-line',
                  label: 'Dirección',
                  value: 'Venezuela',
                  sub: 'Industria Petrolera Nacional',
                },
                {
                  icon: 'ri-whatsapp-line',
                  label: 'Teléfono / WhatsApp',
                  value: '+58 412 833 4239',
                  sub: 'Lunes a Viernes 8am - 5pm',
                },
                {
                  icon: 'ri-mail-line',
                  label: 'Correo Electrónico',
                  value: 'ecruz@polyalphas.net',
                  sub: 'Respondemos en 24 horas',
                },
                {
                  icon: 'ri-instagram-line',
                  label: 'Instagram',
                  value: '@polyalphas',
                  sub: 'Síguenos para novedades',
                },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-500/15 flex-shrink-0">
                    <i className={`${item.icon} text-primary-400 text-xl`}></i>
                  </div>
                  <div>
                    <div className="text-foreground-500 text-xs font-500 uppercase tracking-wide mb-0.5">{item.label}</div>
                    <div className="text-white font-500 text-sm">{item.value}</div>
                    <div className="text-foreground-600 text-xs">{item.sub}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Social */}
            <div className="flex gap-3">
              {[
                { icon: 'ri-instagram-line', href: 'https://www.instagram.com/polyalphas', label: 'Instagram' },
                { icon: 'ri-whatsapp-line', href: 'https://wa.me/584128334239', label: 'WhatsApp' },
                { icon: 'ri-linkedin-box-line', href: '#', label: 'LinkedIn' },
              ].map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  aria-label={s.label}
                  className="w-11 h-11 flex items-center justify-center rounded-lg bg-background-800 text-foreground-400 hover:bg-primary-500 hover:text-white transition-all duration-200 cursor-pointer"
                >
                  <i className={`${s.icon} text-lg`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Form */}
          <div className={`transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <div className="bg-background-900 rounded-2xl p-6 md:p-8 border border-background-800">
              <h3 className="font-heading font-700 text-xl text-white mb-6">
                Envíanos un Mensaje
              </h3>

              {formStatus === 'success' ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-accent-500/20 mb-4">
                    <i className="ri-check-line text-accent-400 text-3xl"></i>
                  </div>
                  <h4 className="font-heading font-700 text-xl text-white mb-2">¡Mensaje Enviado!</h4>
                  <p className="text-foreground-400 text-sm mb-6">
                    Gracias por contactarnos. Nuestro equipo te responderá en las próximas 24 horas.
                  </p>
                  <button
                    onClick={() => setFormStatus('idle')}
                    className="px-6 py-2.5 rounded-md bg-primary-500 text-white text-sm font-600 cursor-pointer whitespace-nowrap hover:bg-primary-600 transition-colors"
                  >
                    Enviar Otro Mensaje
                  </button>
                </div>
              ) : (
                <form
                  id="d9jojcoh9dsfbuoi6500"
                  ref={formRef}
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="space-y-4"
                >
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-foreground-400 text-xs font-500 mb-1.5 uppercase tracking-wide">
                        Nombre *
                      </label>
                      <input
                        type="text"
                        name="nombre"
                        required
                        placeholder="Tu nombre completo"
                        className="w-full px-4 py-3 rounded-lg bg-background-800 border border-background-700 text-white text-sm placeholder-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-foreground-400 text-xs font-500 mb-1.5 uppercase tracking-wide">
                        Empresa
                      </label>
                      <input
                        type="text"
                        name="empresa"
                        placeholder="Nombre de tu empresa"
                        className="w-full px-4 py-3 rounded-lg bg-background-800 border border-background-700 text-white text-sm placeholder-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-foreground-400 text-xs font-500 mb-1.5 uppercase tracking-wide">
                      Correo Electrónico *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="tu@correo.com"
                      className="w-full px-4 py-3 rounded-lg bg-background-800 border border-background-700 text-white text-sm placeholder-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-foreground-400 text-xs font-500 mb-1.5 uppercase tracking-wide">
                      Teléfono / WhatsApp
                    </label>
                    <input
                      type="tel"
                      name="telefono"
                      placeholder="+58 412 833 4239"
                      className="w-full px-4 py-3 rounded-lg bg-background-800 border border-background-700 text-white text-sm placeholder-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-foreground-400 text-xs font-500 mb-1.5 uppercase tracking-wide">
                      Producto de Interés
                    </label>
                    <select
                      name="producto"
                      className="w-full px-4 py-3 rounded-lg bg-background-800 border border-background-700 text-white text-sm focus:outline-none focus:border-primary-500 transition-colors cursor-pointer"
                    >
                      <option value="">Selecciona un producto</option>
                      <option value="ALPHA-29">ALPHA-29 - Demulsificante</option>
                      <option value="ALPHAS-28">ANTIESPUMANTE ALPHAS-28 para Crudo</option>
                      <option value="ALPHA-42">Secuestrante de Oxígeno ALPHA-42</option>
                      <option value="NIVELADOR-PH">Nivelador de pH ALPHA</option>
                      <option value="ALPHA-46">Biocida ALPHA-46</option>
                      <option value="ALPHA-43">Inhibidor de Corrosión ALPHA-43</option>
                      <option value="ALPHA-44">Inhibidor de Incrustación ALPHA-44</option>
                      <option value="ALPHA-41">Clarificante de Agua ALPHA-41</option>
                      <option value="Otro">Otro / Consulta General</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-foreground-400 text-xs font-500 mb-1.5 uppercase tracking-wide">
                      Mensaje *
                    </label>
                    <textarea
                      name="mensaje"
                      required
                      rows={4}
                      maxLength={500}
                      placeholder="Describe tu necesidad o consulta..."
                      className="w-full px-4 py-3 rounded-lg bg-background-800 border border-background-700 text-white text-sm placeholder-foreground-600 focus:outline-none focus:border-primary-500 transition-colors resize-none"
                    />
                  </div>

                  {/* Honeypot */}
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />

                  {formStatus === 'error' && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                      <i className="ri-error-warning-line text-red-400 text-sm"></i>
                      <span className="text-red-400 text-sm">{formError}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === 'sending'}
                    className="w-full py-3.5 rounded-lg bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-white font-600 text-sm transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    {formStatus === 'sending' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Enviando...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line"></i>
                        Enviar Mensaje
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}